// Content Guidelines with checkoff functionality

import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Label,
} from "@repo/ui";
import { ChevronDown, ListChecks } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { LinkifiedText } from "@/components/shared/linkified-text";

// ============================================================================
// Types
// ============================================================================

export interface CheckoffGuidelineState {
  type: string;
  instructions: Array<{
    text: string;
    checked: boolean;
  }>;
}

interface ContentGuidelinesProps {
  guidelines: CheckoffGuidelineState[];
  onChange: (guidelines: CheckoffGuidelineState[]) => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Merge duplicate checkoff guidelines by type and filter out empty instructions.
 * This fixes issues where duplicate guideline types cause checkbox rendering problems.
 */
interface CheckoffGuidelineInput {
  type?: string | null;
  instructions?: Array<{
    brandGuidelineCheckoff?: boolean | null;
    creatorGuidelineCheckoff?: boolean | null;
    text?: string | null;
  }> | null;
}

export function mergeCheckoffGuidelines(
  guidelines: CheckoffGuidelineInput[] | null | undefined,
): CheckoffGuidelineState[] {
  if (!guidelines || guidelines.length === 0) return [];

  const merged = guidelines.reduce(
    (acc: CheckoffGuidelineState[], guideline: CheckoffGuidelineInput) => {
      if (!guideline?.type) return acc;

      const existing = acc.find(
        (g: CheckoffGuidelineState) => g.type === guideline.type,
      );
      type Instruction = NonNullable<
        CheckoffGuidelineInput["instructions"]
      >[number];
      const validInstructions = (guideline.instructions ?? []).filter(
        (i: Instruction) => i?.text,
      );

      if (existing) {
        existing.instructions.push(
          ...validInstructions.map((i: Instruction) => ({
            text: i.text!,
            checked: false,
          })),
        );
      } else if (validInstructions.length > 0) {
        acc.push({
          type: guideline.type,
          instructions: validInstructions.map((i: Instruction) => ({
            text: i.text!,
            checked: false,
          })),
        });
      }
      return acc;
    },
    [] as CheckoffGuidelineState[],
  );

  return merged.filter(
    (g: CheckoffGuidelineState) => g.instructions.length > 0,
  );
}

/**
 * Get flat array of all checked states for submission
 */
export function getCheckedOffFlat(
  guidelines: CheckoffGuidelineState[],
): boolean[] {
  return guidelines.flatMap((g) => g.instructions.map((i) => i.checked));
}

/**
 * Get unchecked guidelines for confirmation modal
 */
export function getUncheckedGuidelines(
  guidelines: CheckoffGuidelineState[],
): CheckoffGuidelineState[] {
  return guidelines
    .map((g) => ({
      type: g.type,
      instructions: g.instructions.filter((i) => !i.checked),
    }))
    .filter((g) => g.instructions.length > 0);
}

// ============================================================================
// Component
// ============================================================================

export function ContentGuidelines({
  guidelines,
  onChange,
}: ContentGuidelinesProps) {
  const handleGuidelineChange = useCallback(
    (guidelineType: string, instructionIndex: number, checked: boolean) => {
      const updated = guidelines.map((g) => {
        if (g.type !== guidelineType) return g;
        return {
          ...g,
          instructions: g.instructions.map((inst, idx) =>
            idx === instructionIndex ? { ...inst, checked } : inst,
          ),
        };
      });
      onChange(updated);
    },
    [guidelines, onChange],
  );

  if (guidelines.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-muted-foreground" />
        <Label className="font-semibold">Content Guidelines</Label>
      </div>
      <p className="text-sm text-muted-foreground">
        Check off all guidelines to confirm they were completed.
      </p>
      <div className="space-y-2 border rounded-lg p-3">
        {guidelines.map((guideline) => (
          <Collapsible key={guideline.type} defaultOpen>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-1 text-sm font-medium hover:text-foreground/80">
              {guideline.type}
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              {guideline.instructions.map((instruction, idx) => (
                <label
                  key={`${guideline.type}-${instruction.text}`}
                  className="flex items-start gap-3 text-sm cursor-pointer pl-2"
                >
                  <Checkbox
                    checked={instruction.checked}
                    onCheckedChange={(checked) =>
                      handleGuidelineChange(
                        guideline.type,
                        idx,
                        checked === true,
                      )
                    }
                    className="mt-0.5"
                  />
                  <span className="text-muted-foreground">
                    <LinkifiedText text={instruction.text} />
                  </span>
                </label>
              ))}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Hook for managing guidelines state
// ============================================================================

export function useContentGuidelines(
  checkoffGuidelines: CheckoffGuidelineInput[] | null | undefined,
) {
  const initialGuidelines = useMemo(
    () => mergeCheckoffGuidelines(checkoffGuidelines),
    [checkoffGuidelines],
  );

  const [guidelines, setGuidelines] =
    useState<CheckoffGuidelineState[]>(initialGuidelines);

  const reset = useCallback(() => {
    setGuidelines(initialGuidelines);
  }, [initialGuidelines]);

  const hasGuidelines = initialGuidelines.length > 0;
  const uncheckedGuidelines = useMemo(
    () => getUncheckedGuidelines(guidelines),
    [guidelines],
  );
  const checkedOffFlat = useMemo(
    () => getCheckedOffFlat(guidelines),
    [guidelines],
  );

  return {
    guidelines,
    setGuidelines,
    reset,
    hasGuidelines,
    uncheckedGuidelines,
    checkedOffFlat,
  };
}
